import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Col, Form, ProgressBar, Row, Spinner } from 'react-bootstrap';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import FormImovel from './FormImovel';
import ModalCopiarImovel from './ModalCopiarImovel';
import { numeroParaCampo } from './formatos';
import { CAMPOS_BOOLEANOS, CAMPOS_DINHEIRO, CAMPOS_TEXTO, PADROES_NOVO, PADROES_NOVO_BOOLEANOS } from './constantes';

function valoresIniciais(imovel) {
  const v = {};
  for (const campo of CAMPOS_TEXTO) {
    const bruto = imovel ? imovel[campo] : PADROES_NOVO[campo];
    // o legado gravava " " (um espaço) como "vazio" em vários selects
    v[campo] = bruto === null || bruto === undefined || String(bruto).trim() === '' ? '' : String(bruto);
  }
  for (const campo of CAMPOS_DINHEIRO) v[campo] = imovel ? numeroParaCampo(imovel[campo]) : '';
  for (const campo of CAMPOS_BOOLEANOS) {
    v[campo] = imovel ? imovel[campo] === 'true' : PADROES_NOVO_BOOLEANOS.includes(campo);
  }
  return v;
}

// Cria/edita o imóvel. Passos do "Salvar" (mesma ordem do legado): grava os dados
// (+ características), depois envia as fotos novas.
export default function PaginaImovel() {
  const { publicId } = useParams();
  const ehNovo = !publicId;
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [copiando, setCopiando] = useState(false); // modal "Copiar imóvel"
  const toast = useToast();

  const [imovel, setImovel] = useState(null);
  const [v, setValores] = useState(() => valoresIniciais(null));
  const [proprietario, setProprietario] = useState(null);
  const [captador, setCaptador] = useState(ehNovo ? usuario.id : '');
  const [caracteristicas, setCaracteristicas] = useState(new Set());
  const [fotos, setFotos] = useState([]);
  const [pendentes, setPendentes] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [captadores, setCaptadores] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [carregando, setCarregando] = useState(!ehNovo);
  const [erroCarga, setErroCarga] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [progresso, setProgresso] = useState(null);
  const [erroCampos, setErroCampos] = useState('');
  const [sujo, setSujo] = useState(false);

  const setV = useCallback((nome, valor) => {
    setValores((atual) => ({ ...atual, [nome]: valor }));
    setSujo(true);
  }, []);

  function aplicarImovel(dados) {
    setImovel(dados);
    setValores(valoresIniciais(dados));
    setProprietario(dados.proprietario ? { public_id: dados.proprietario.public_id, nome: dados.proprietario.nome, celular: dados.proprietario.celular } : null);
    setCaptador(dados.captador || '');
    setCaracteristicas(new Set(dados.caracteristicas));
    setFotos(dados.imagens);
    setSujo(false);
  }

  // Carrega o imóvel (edição). A empresa dele define os dados de apoio (nivel 1).
  useEffect(() => {
    if (ehNovo) return;
    let cancelado = false;
    setCarregando(true);
    api
      .get(`/imoveis/${publicId}`)
      .then((r) => {
        if (cancelado) return;
        aplicarImovel(r.data);
        setEmpresaId(r.data.empresa_id || '');
      })
      .catch((err) => {
        if (cancelado) return;
        setErroCarga(true);
        toast.erro(err.message || 'Não foi possível carregar o imóvel');
      })
      .finally(() => !cancelado && setCarregando(false));
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId]);

  // superadmin cadastrando: precisa escolher a empresa antes
  useEffect(() => {
    if (ehNovo && usuario.nivel === 1) api.get('/empresas').then((r) => setEmpresas(r.data)).catch(() => {});
  }, [ehNovo, usuario.nivel]);

  // tipos + captadores (nivel 1 depende da empresa escolhida)
  useEffect(() => {
    if (usuario.nivel === 1 && !empresaId) {
      api.get('/imoveis/apoio').then((r) => setTipos(r.data.tipos)).catch(() => {});
      return;
    }
    const q = usuario.nivel === 1 ? `?empresa_id=${empresaId}` : '';
    api
      .get(`/imoveis/apoio${q}`)
      .then((r) => {
        setTipos(r.data.tipos);
        setCaptadores(r.data.captadores);
      })
      .catch((err) => toast.erro(err.message || 'Não foi possível carregar os dados do formulário'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario.nivel, empresaId]);

  // avisa antes de fechar a aba com alterações não salvas (o legado também avisava)
  useEffect(() => {
    if (!sujo && pendentes.length === 0) return undefined;
    const aviso = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', aviso);
    return () => window.removeEventListener('beforeunload', aviso);
  }, [sujo, pendentes.length]);

  function montarCorpo() {
    const corpo = { ...v };
    corpo.proprietario = proprietario ? proprietario.public_id : null;
    if (captador) corpo.captador = captador;
    corpo.caracteristicas = Array.from(caracteristicas);
    if (ehNovo && usuario.nivel === 1) corpo.empresa_id = empresaId;
    return corpo;
  }

  async function enviarFotos(idPublico) {
    const falhas = [];
    for (let i = 0; i < pendentes.length; i++) {
      setProgresso({ atual: i + 1, total: pendentes.length });
      const formulario = new FormData();
      formulario.append('imagem', pendentes[i]);
      try {
        await api.upload(`/imoveis/${idPublico}/imagens`, formulario);
      } catch (err) {
        falhas.push(`${pendentes[i].name}: ${err.message}`);
      }
    }
    setProgresso(null);
    return falhas;
  }

  async function salvar() {
    setErroCampos('');
    if (ehNovo && usuario.nivel === 1 && !empresaId) {
      setErroCampos('Escolha a empresa do imóvel.');
      return;
    }
    if (!v.i1_tipo) {
      setErroCampos('Escolha o tipo do imóvel.');
      return;
    }
    setSalvando(true);
    try {
      const r = ehNovo ? await api.post('/imoveis', montarCorpo()) : await api.put(`/imoveis/${publicId}`, montarCorpo());
      const idPublico = r.data.public_id;

      let falhas = [];
      if (pendentes.length > 0) falhas = await enviarFotos(idPublico);
      setPendentes([]);

      toast.sucesso(ehNovo ? 'Imóvel adicionado com sucesso!' : 'Imóvel alterado com sucesso!');
      if (falhas.length > 0) toast.erro(`Algumas fotos não foram enviadas: ${falhas.join(' | ')}`);

      if (ehNovo) {
        setSujo(false);
        navigate(`/imoveis/${idPublico}`, { replace: true });
      } else {
        // recarrega (fotos novas + rascunho recalculado pelo servidor)
        const atualizado = await api.get(`/imoveis/${idPublico}`);
        aplicarImovel(atualizado.data);
      }
    } catch (err) {
      setErroCampos(err.message || 'Não foi possível salvar');
      toast.erro(err.message || 'Não foi possível salvar');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) return <Spinner animation="border" />;
  if (erroCarga) return null;

  const podeEditar = ehNovo ? true : !!imovel?.pode_editar;
  const precisaEscolherEmpresa = ehNovo && usuario.nivel === 1;

  return (
    <>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h1 className="h4 mb-0">{ehNovo ? 'Novo imóvel' : 'Editar imóvel'}</h1>
        {!ehNovo && imovel && usuario.permissoes?.imoveis_criar === true && (
          <Button variant="outline-secondary" size="sm" onClick={() => setCopiando(true)} disabled={sujo} title={sujo ? 'Salve as alterações antes de copiar' : 'Criar um imóvel novo igual a este'}>
            <i className="bi bi-files" /> Copiar imóvel
          </Button>
        )}
      </div>
      {!ehNovo && imovel && (
        <ModalCopiarImovel
          show={copiando}
          aoFechar={() => setCopiando(false)}
          publicId={imovel.public_id}
          codigo={imovel.i1_codsequencia}
          totalFotos={imovel.imagens?.length}
          aoCopiado={(novo) => { setCopiando(false); navigate(`/imoveis/${novo.public_id}`); }}
        />
      )}

      {precisaEscolherEmpresa && (
        <Row className="mb-3">
          <Col md={5}>
            <Form.Group controlId="empresa-do-imovel">
              <Form.Label>Empresa do imóvel</Form.Label>
              <Form.Select value={empresaId} onChange={(e) => { setEmpresaId(e.target.value); setProprietario(null); setCaptador(''); }}>
                <option value="">Selecione...</option>
                {empresas.map((emp) => (
                  <option key={emp.public_id} value={emp.public_id}>
                    {emp.e1_nome}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      )}

      {progresso && (
        <Alert variant="info">
          Enviando foto {progresso.atual} de {progresso.total}...
          <ProgressBar className="mt-2" now={(progresso.atual / progresso.total) * 100} />
        </Alert>
      )}

      <FormImovel
        temAlteracoes={sujo}
        aoTrocarTipo={aplicarImovel}
        aoCopiarImovel={usuario.permissoes?.imoveis_criar === true ? () => setCopiando(true) : undefined}
        ehNovo={ehNovo}
        v={v}
        setV={setV}
        imovel={imovel}
        proprietario={proprietario}
        setProprietario={(p) => { setProprietario(p); setSujo(true); }}
        captador={captador}
        setCaptador={(c) => { setCaptador(c); setSujo(true); }}
        tipos={tipos}
        captadores={captadores}
        caracteristicas={caracteristicas}
        setCaracteristicas={(s) => { setCaracteristicas(s); setSujo(true); }}
        fotos={fotos}
        setFotos={setFotos}
        pendentes={pendentes}
        setPendentes={setPendentes}
        empresaId={usuario.nivel === 1 ? empresaId : ''}
        podeEditar={podeEditar}
        salvando={salvando}
        aoSalvar={salvar}
        erroCampos={erroCampos}
      />
    </>
  );
}
